import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";

import { supabase } from "../lib/supabase";

type LocationState = { from?: string };

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from ?? "/home";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function signIn(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setBusy(false);
    if (error) {
      setMessage(
        "Sign-in was not accepted. Check your details or ask Teo or Cathy for access.",
      );
      return;
    }
    void navigate(from, { replace: true });
  }

  async function signInWithGoogle() {
    setBusy(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}${from}` },
    });
    if (error) {
      setBusy(false);
      setMessage("Google sign-in could not be started. Please try again.");
    }
  }

  if (location.hash.includes("access_token"))
    return <Navigate to={from} replace />;

  return (
    <main className="page grid min-h-screen place-items-center">
      <section
        className="card w-full max-w-md p-7"
        aria-labelledby="sign-in-title"
      >
        <p className="mb-2 text-sm font-bold text-sky-deep">
          Private support space
        </p>
        <h1 id="sign-in-title" className="text-3xl font-bold">
          Welcome to Teo's Support Hub
        </h1>
        <p className="mt-3 text-muted">
          Sign in with the account Teo or Cathy authorised for you.
        </p>
        <form
          className="mt-7 grid gap-4"
          onSubmit={(event) => void signIn(event)}
        >
          <label className="grid gap-1 font-semibold">
            Email address
            <input
              className="rounded-xl border border-slate-300 px-3"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label className="grid gap-1 font-semibold">
            Password
            <input
              className="rounded-xl border border-slate-300 px-3"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          {message ? (
            <p
              role="alert"
              className="rounded-xl bg-warning-soft p-3 text-warning"
            >
              {message}
            </p>
          ) : null}
          <button className="button-primary" disabled={busy} type="submit">
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <button
            className="button-secondary"
            disabled={busy}
            type="button"
            onClick={() => void signInWithGoogle()}
          >
            Continue with Google
          </button>
        </form>
        <p className="mt-6 text-sm text-muted">
          Accounts are invite-only. There is no public registration.
        </p>
      </section>
    </main>
  );
}
